import {Flex} from "../../components/Container/Flex";
import {DeviceAPI} from "../../utils/API/DeviceAPI";
import React from 'react';
import Device, {fieldDisplayName, isReadOnlyField} from "../../utils/models/Device";
import TextField from "../../components/Controls/TextField";
import {useTranslation} from "react-i18next";
import Dialog from "../../components/Dialog";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import TextButton from "../../components/Controls/TextButton";
import {formatDateTime} from "../../utils/time";
import dayjs from "dayjs";
import Select from "../../components/Controls/Select";
import {Text} from "../../utils/style/texts";

const api = new DeviceAPI();

const DeviceForm = props => {
    const [device, setDevice] = React.useState(null);
    const [url, setUrl] = React.useState(null);
    const [isLoading, setLoading] = React.useState(false);
    const [t] = useTranslation();
    const [alarmProtocols, setAlarmProtocols] = React.useState([]);

    React.useEffect(() => {
        if (device) return;
        api.getDeviceByUrl().then(({url, device, alarmProtocols}) => {
            setUrl(url);
            setDevice(new Device(device));
            setAlarmProtocols(alarmProtocols.map(p => {
                return p
            }));
        });
    }, [device]);

    if (!device || !url) return null;
    const canSave = device.name?.length > 3;

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{url.name}</DialogTitle>
            <DialogContent>
                <Flex col gap={6}>
                    <TextField
                        label={t('deviceName')}
                        fullWidth
                        value={device.name}
                        disabled={isLoading || !url.deviceName}
                        onChange={v => setDevice(device.set('name', v))}
                    />
                    {!!url.operatingAddress && (
                        <TextField
                            label={t('operatingAddress')}
                            disabled={isLoading}
                            fullWidth
                            value={device.operatingAddress}
                            onChange={v => setDevice(device.set('operatingAddress', v))}
                        />
                    )}
                    {!!url.tempAddress && (
                        <TextField
                            label={t('tempAddress')}
                            helperText={device.tempAddressExpiration ? `${t('availableUntil')}: ${formatDateTime(device.tempAddressExpirationDate)}` : undefined}
                            style={{marginBottom: 12}}
                            disabled={isLoading}
                            fullWidth
                            value={device.tempAddress}
                            onChange={v => setDevice(device.set('tempAddress', v))}
                        />
                    )}
                    {!!url.industry && (
                        <TextField
                            label={t('industry')}
                            disabled={isLoading}
                            fullWidth
                            value={device.industry}
                            onChange={v => setDevice(device.set('industry', v))}
                        />
                    )}
                    {!!url.extra && device.get('extraFields').map((f, i) => {
                        if (!url.extra[f.name]) return null;
                        const readOnly = isReadOnlyField(f);
                        return (
                            <TextField
                                label={fieldDisplayName(f.name)}
                                key={f.name}
                                disabled={isLoading || readOnly}
                                fullWidth
                                helperText={!!f.value && !!f.exp && `${t('availableUntil')}: ${formatDateTime(dayjs.unix(f.exp))}`}
                                value={f.value}
                                style={!!f.value && !!f.exp ? {marginBottom: 12} : {}}
                                onChange={value => {
                                    const newFields = [...device.get('extraFields')];
                                    newFields[i] = {...f, value};
                                    setDevice(device.set('extraFields', newFields));
                                }}
                            />
                        )
                    })}

                    {!!url.alarmProtocol && (
                        <>
                            <Select
                                label={t('alarmProtocol')}
                                editable={!isLoading}
                                fullWidth
                                value={device.protocolId ?? "0"}
                                onChange={v => setDevice(device.set('protocolId', v))}
                                items={[{id: "0", name: t("none")}, ...alarmProtocols].map(p => {
                                    return {
                                        value: p.id,
                                        label: p.name + (p.isDefault ? ` (${t('default').lc()})` : ''),
                                    }
                                })}
                            />
                        </>
                    )}
                </Flex>
            </DialogContent>
            <DialogActions>
                <TextButton
                    color="primary"
                    loading={isLoading}
                    disabled={!canSave}
                    onClick={() => {
                        setLoading(true);
                        api.saveDeviceByUrl(device).then(() => window.location.reload());
                    }}
                >
                    {t('save')}
                </TextButton>
            </DialogActions>
        </Dialog>
    );

};

export default DeviceForm;